import React, { useCallback, useEffect, useRef, useState } from 'react';
import TabbedSidebar from '../tabbedSidebar/TabbedSidebar';
import GarmentPreview from './GarmentPreview';
import GarmentPanel from './GarmentPanel';
import PatternPanel from './PatternPanel';
import StripePanel from './StripePanel';
import LogoPanel from './LogoPanel';
import TextPlacerPanel from './TextPlacerPanel';
import { NUMBER_CONFIG, NAME_CONFIG } from './textConfigs';
import exportPng from './exportPng';
import { GARMENTS, PATTERNS, SOLID_PATTERN_ID } from './assetManifest';
import { useSvgAsset } from './useSvg';
import { resolveColors } from './svgColors';
import { DEFAULT_PATTERN_COLORS, DEFAULT_STRIPE_COLOR } from './colorPalette';
import './UniformBuilder.css';
import bg from '../../img/bg-3.png';
import sportstarLogo from './sportstar-logo.svg';

// Gold in every slot (no stripe file has more than a few colours).
const INITIAL_STRIPE_COLORS = Array(8).fill(DEFAULT_STRIPE_COLOR);

// Tab rail icon n (1-6): a div showing that row of the sprite sheet (see UniformBuilder.css).
const TabIcon = ({ n }) => <div className={`ub-tab-icon ub-tab-icon-${n}`} />;

// Immutable "set array[index] = value" that tolerates holes.
const setAt = (arr = [], index, value) => {
    const next = [...arr];
    next[index] = value;
    return next;
};

/**
 * UniformBuilder
 *
 * Garment builder driven entirely by the files in ./assets (see assetManifest).
 * Same three-pane layout as tabbedSidebar: tab rail, config panel, canvas.
 */
export default function UniformBuilder() {
    const svgRef = useRef(null);

    // Lets UniformBuilder.css hide the site footer's banner + links on this page only.
    useEffect(() => {
        document.body.classList.add('uniform-builder-page');
        return () => document.body.classList.remove('uniform-builder-page');
    }, []);

    const [garmentSlug, setGarmentSlug] = useState(GARMENTS.length ? GARMENTS[0].slug : null);
    const [patternId, setPatternId] = useState(SOLID_PATTERN_ID);
    // One colour per slot, shared by every pattern (and by pattern-filled stripes).
    const [bodyColors, setBodyColors] = useState(DEFAULT_PATTERN_COLORS);
    // Colour a newly added stripe starts with: gold, then whatever was last chosen.
    const [stripeDefaults, setStripeDefaults] = useState(INITIAL_STRIPE_COLORS);
    const [stripeIds, setStripeIds] = useState([]);       // enabled, in stacking order
    const [stripeConfigs, setStripeConfigs] = useState({}); // { [id]: { patternId, colors, patternColors } }
    const [logos, setLogos] = useState([]);
    const [numbers, setNumbers] = useState([]);
    const [names, setNames] = useState([]);

    const garment = GARMENTS.find((g) => g.slug === garmentSlug) || null;

    // Canvas backdrop follows the first colour of the body pattern.
    const bodyPattern = PATTERNS.find((p) => p.id === patternId) || null;
    const bodyAsset = useSvgAsset(bodyPattern ? bodyPattern.url : null);
    const backdrop = bodyAsset ? resolveColors(bodyAsset.slots, bodyColors)[0] : undefined;

    const updateStripe = (id, patch) =>
        setStripeConfigs((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

    const handleGarmentSelect = (slug) => {
        if (slug === garmentSlug) return;
        setGarmentSlug(slug);
        setStripeIds([]);       // stripes and placements belong to one garment;
        setLogos([]);           // stripe colours are kept in stripeConfigs
        setNumbers([]);
        setNames([]);
    };

    // Add / remove / patch helpers for a list held in state.
    const listHandlers = (setList) => ({
        onAdd: (item) => setList((prev) => [...prev, item]),
        onRemove: (id) => setList((prev) => prev.filter((x) => x.id !== id)),
        onUpdate: (id, patch) => setList((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x))),
    });

    const handleBodyColor = (slot, hex) => setBodyColors((prev) => setAt(prev, slot, hex));

    const handleStripeToggle = (id) => {
        if (!stripeIds.includes(id)) {
            // Freeze the current default so later colour changes only affect later stripes.
            setStripeConfigs((prev) => (
                prev[id] && prev[id].colors ? prev : { ...prev, [id]: { ...(prev[id] || {}), colors: stripeDefaults } }
            ));
        }
        setStripeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleStripeFill = (id, pid) => updateStripe(id, { patternId: pid });

    const handleStripeColor = (id, slot, hex) => {
        setStripeDefaults((prev) => setAt(prev, slot, hex));
        setStripeConfigs((prev) => {
            const cfg = prev[id] || {};
            return { ...prev, [id]: { ...cfg, colors: setAt(cfg.colors || stripeDefaults, slot, hex) } };
        });
    };

    const handleStripePatternColor = (id, pid, slot, hex) =>
        setStripeConfigs((prev) => {
            const cfg = prev[id] || {};
            const pc = cfg.patternColors || {};
            return { ...prev, [id]: { ...cfg, patternColors: { ...pc, [pid]: setAt(pc[pid], slot, hex) } } };
        });

    const handleDownload = useCallback(
        () => exportPng(svgRef.current, `${garmentSlug || 'garment'}-design.png`),
        [garmentSlug]
    );

    if (!garment) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100vh', background: '#121212', color: '#ff5050', fontSize: '14px',
            }}>
                No garments found in uniformBuilder/assets/garments.
            </div>
        );
    }

    const tabs = [
        {
            id: 1,
            icon: <TabIcon n={1} />,
            label: 'Garment',
            content: (
                <GarmentPanel garments={GARMENTS} selectedId={garment.slug} onSelect={handleGarmentSelect} />
            ),
        },
        {
            id: 2,
            icon: <TabIcon n={2} />,
            label: 'Pattern',
            content: (
                <PatternPanel
                    patterns={PATTERNS}
                    patternId={patternId}
                    bodyColors={bodyColors}
                    onPatternChange={setPatternId}
                    onColorChange={handleBodyColor}
                />
            ),
        },
        {
            id: 3,
            icon: <TabIcon n={3} />,
            label: 'Stripes',
            content: (
                <StripePanel
                    key={garment.slug}
                    stripes={garment.stripes}
                    patterns={PATTERNS}
                    enabledIds={stripeIds}
                    stripeConfigs={stripeConfigs}
                    bodyColors={bodyColors}
                    stripeDefaults={stripeDefaults}
                    onToggle={handleStripeToggle}
                    onFillChange={handleStripeFill}
                    onColorChange={handleStripeColor}
                    onPatternColorChange={handleStripePatternColor}
                />
            ),
        },
        {
            id: 4,
            icon: <TabIcon n={4} />,
            label: 'Logos',
            content: <LogoPanel logos={logos} {...listHandlers(setLogos)} />,
        },
        {
            id: 5,
            icon: <TabIcon n={5} />,
            label: 'Numbers',
            content: <TextPlacerPanel items={numbers} config={NUMBER_CONFIG} {...listHandlers(setNumbers)} />,
        },
        {
            id: 6,
            icon: <TabIcon n={6} />,
            label: 'Names',
            content: <TextPlacerPanel items={names} config={NAME_CONFIG} {...listHandlers(setNames)} />,
        },
    ];

    return (
        <TabbedSidebar tabs={tabs} defaultTab={1}>
            <div style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${bg})`,
                backgroundColor: backdrop,
            }}>
                {/* ── Branded header bar ── */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 20px',
                    pointerEvents: 'none',
                    zIndex: 1,
                }}>
                    <img src={sportstarLogo} alt="Sportstar" style={{ height: '70px', display: 'block' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', pointerEvents: 'auto' }}>
                        <a
                            href="mailto:sales@sportstar.co"
                            style={{
                                color: '#ecff19',
                                fontSize: '12px',
                                fontWeight: 600,
                                textDecoration: 'none',
                                letterSpacing: '0.03em',
                            }}
                        >
                            Contact: sales@sportstar.co
                        </a>
                        <button className="screenshot-btn" onClick={handleDownload} title="Download PNG">
                            ↓ Download PNG
                        </button>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '24px',
                    boxSizing: 'border-box',
                }}>
                    <GarmentPreview
                        svgRef={svgRef}
                        garment={garment}
                        patterns={PATTERNS}
                        patternId={patternId}
                        bodyColors={bodyColors}
                        stripeDefaults={stripeDefaults}
                        stripeIds={stripeIds}
                        stripeConfigs={stripeConfigs}
                        logos={logos}
                        numbers={numbers}
                        names={names}
                    />
                </div>
            </div>
        </TabbedSidebar>
    );
}
